import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { logger } from '../../shared/utils/logger.js';
import {
  employeesApi,
  type AssignmentRow,
  type AssignmentWriteInput,
  type EmployeeDocumentAccessGrant,
  type EmployeeDocumentAccessOtpIssue,
  type EmployeeDocumentRow,
  type EmployeeDocumentUploadInput,
  type EmployeeListFilters,
  type EmployeeRoleRow,
  type EmployeeRoleWriteInput,
  type EmployeeRow,
  type EmployeeScheduleDetail,
  type EmployeeScheduleExceptionWriteInput,
  type EmployeeScheduleWriteInput,
  type EmployeeStatusInput,
  type EmployeeWriteInput,
  type ServicePickerRow,
} from './api/employees.api';

export const employeesKeys = {
  all: ['employees-module'] as const,
  roles: (statusFilter?: string) => [...employeesKeys.all, 'roles', statusFilter || ''] as const,
  employees: (filters: EmployeeListFilters = {}) =>
    [...employeesKeys.all, 'employees', filters.status || '', filters.roleUuid || ''] as const,
  employee: (employeeUuid: string) =>
    [...employeesKeys.all, 'employees', 'detail', employeeUuid] as const,
  employeeDocuments: (employeeUuid: string) =>
    [...employeesKeys.all, 'employees', 'documents', employeeUuid] as const,
  schedule: (employeeUuid: string) =>
    [...employeesKeys.all, 'schedule', employeeUuid] as const,
  employeeAssignments: (employeeUuid: string) =>
    [...employeesKeys.all, 'assignments', 'by-employee', employeeUuid] as const,
  serviceAssignments: (serviceUuid: string) =>
    [...employeesKeys.all, 'assignments', 'by-service', serviceUuid] as const,
  servicePicker: () =>
    [...employeesKeys.all, 'service-picker'] as const,
};

const MODULE = 'employees-roles-queries';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const invalidateRoles = (queryClient: ReturnType<typeof useQueryClient>): void => {
  queryClient.invalidateQueries({ queryKey: [...employeesKeys.all, 'roles'] });
};

const invalidateEmployees = (queryClient: ReturnType<typeof useQueryClient>): void => {
  queryClient.invalidateQueries({ queryKey: [...employeesKeys.all, 'employees'] });
};

const invalidateEmployeeDocuments = (
  queryClient: ReturnType<typeof useQueryClient>,
  employeeUuid: string,
): void => {
  queryClient.invalidateQueries({ queryKey: employeesKeys.employeeDocuments(employeeUuid) });
  queryClient.invalidateQueries({ queryKey: employeesKeys.employee(employeeUuid) });
};

const invalidateAssignmentsTree = (queryClient: ReturnType<typeof useQueryClient>): void => {
  queryClient.invalidateQueries({ queryKey: [...employeesKeys.all, 'assignments'] });
};

export const useEmployeeRoles = (enabled: boolean, statusFilter?: string) =>
  useQuery<EmployeeRoleRow[]>({
    queryKey: employeesKeys.roles(statusFilter),
    queryFn: () => employeesApi.listRoles(statusFilter),
    enabled,
    staleTime: 30_000,
  });

export const useCreateEmployeeRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeRoleWriteInput) => employeesApi.createRole(input),
    onSuccess: (row) => {
      logEvent('employee_role_create_success', { roleUuid: row.roleUuid });
      invalidateRoles(queryClient);
    },
    onError: (error) => {
      logEvent('employee_role_create_error', { message: (error as Error).message });
    },
  });
};

export const useUpdateEmployeeRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleUuid, input }: { roleUuid: string; input: EmployeeRoleWriteInput }) =>
      employeesApi.updateRole(roleUuid, input),
    onSuccess: (row) => {
      logEvent('employee_role_update_success', { roleUuid: row.roleUuid });
      invalidateRoles(queryClient);
    },
    onError: (error) => {
      logEvent('employee_role_update_error', { message: (error as Error).message });
    },
  });
};

export const useToggleEmployeeRoleStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleUuid, status }: { roleUuid: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      employeesApi.updateRoleStatus(roleUuid, status),
    onSuccess: (row) => {
      logEvent('employee_role_status_success', { roleUuid: row.roleUuid, status: row.status });
      invalidateRoles(queryClient);
    },
    onError: (error) => {
      logEvent('employee_role_status_error', { message: (error as Error).message });
    },
  });
};

export const useDeleteEmployeeRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleUuid: string) => employeesApi.deleteRole(roleUuid),
    onSuccess: (_, roleUuid) => {
      logEvent('employee_role_delete_success', { roleUuid });
      invalidateRoles(queryClient);
    },
    onError: (error) => {
      logEvent('employee_role_delete_error', { message: (error as Error).message });
    },
  });
};

// ---------- C5: Employees ----------

export const useEmployees = (enabled: boolean, filters: EmployeeListFilters = {}) =>
  useQuery<EmployeeRow[]>({
    queryKey: employeesKeys.employees(filters),
    queryFn: () => employeesApi.listEmployees(filters),
    enabled,
    staleTime: 30_000,
  });

export const useEmployee = (employeeUuid: string | null) =>
  useQuery<EmployeeRow>({
    queryKey: employeesKeys.employee(employeeUuid || ''),
    queryFn: () => employeesApi.getEmployee(employeeUuid as string),
    enabled: Boolean(employeeUuid),
    staleTime: 30_000,
  });

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeWriteInput) => employeesApi.createEmployee(input),
    onSuccess: (row) => {
      logEvent('employee_create_success', { employeeUuid: row.employeeUuid });
      invalidateEmployees(queryClient);
    },
    onError: (error) => {
      logEvent('employee_create_error', { message: (error as Error).message });
    },
  });
};

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeUuid, input }: { employeeUuid: string; input: EmployeeWriteInput }) =>
      employeesApi.updateEmployee(employeeUuid, input),
    onSuccess: (row) => {
      logEvent('employee_update_success', { employeeUuid: row.employeeUuid });
      invalidateEmployees(queryClient);
      queryClient.invalidateQueries({ queryKey: employeesKeys.employee(row.employeeUuid) });
    },
    onError: (error) => {
      logEvent('employee_update_error', { message: (error as Error).message });
    },
  });
};

export const useUpdateEmployeeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeUuid, status }: { employeeUuid: string; status: EmployeeStatusInput['status'] }) =>
      employeesApi.updateEmployeeStatus(employeeUuid, status),
    onSuccess: (row) => {
      logEvent('employee_status_success', { employeeUuid: row.employeeUuid, status: row.status });
      invalidateEmployees(queryClient);
      invalidateAssignmentsTree(queryClient);
      queryClient.invalidateQueries({ queryKey: employeesKeys.employee(row.employeeUuid) });
    },
    onError: (error) => {
      logEvent('employee_status_error', { message: (error as Error).message });
    },
  });
};

export const useUpdateEmployeeRoleAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeUuid, roleUuid }: { employeeUuid: string; roleUuid: string }) =>
      employeesApi.updateEmployeeRole(employeeUuid, roleUuid),
    onSuccess: (row) => {
      logEvent('employee_role_assignment_success', {
        employeeUuid: row.employeeUuid,
        roleUuid: row.role?.roleUuid || null,
      });
      invalidateEmployees(queryClient);
      queryClient.invalidateQueries({ queryKey: employeesKeys.employee(row.employeeUuid) });
    },
    onError: (error) => {
      logEvent('employee_role_assignment_error', { message: (error as Error).message });
    },
  });
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeUuid: string) => employeesApi.deleteEmployee(employeeUuid),
    onSuccess: (_, employeeUuid) => {
      logEvent('employee_delete_success', { employeeUuid });
      invalidateEmployees(queryClient);
      invalidateAssignmentsTree(queryClient);
    },
    onError: (error) => {
      logEvent('employee_delete_error', { message: (error as Error).message });
    },
  });
};

export const useEmployeeDocuments = (employeeUuid: string | null) =>
  useQuery<EmployeeDocumentRow[]>({
    queryKey: employeesKeys.employeeDocuments(employeeUuid || ''),
    queryFn: () => employeesApi.listEmployeeDocuments(employeeUuid as string),
    enabled: Boolean(employeeUuid),
    staleTime: 30_000,
  });

export const useUploadEmployeeDocument = (employeeUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeDocumentUploadInput) =>
      employeesApi.uploadEmployeeDocument(employeeUuid, input),
    onSuccess: (row) => {
      logEvent('employee_document_upload_success', {
        employeeUuid: row.employeeUuid,
        documentUuid: row.documentUuid,
        documentType: row.documentType,
      });
      invalidateEmployeeDocuments(queryClient, row.employeeUuid);
    },
    onError: (error) => {
      logEvent('employee_document_upload_error', {
        employeeUuid,
        message: (error as Error).message,
      });
    },
  });
};

export const useDeleteEmployeeDocument = (employeeUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentUuid: string) =>
      employeesApi.deleteEmployeeDocument(employeeUuid, documentUuid),
    onSuccess: (_, documentUuid) => {
      logEvent('employee_document_delete_success', { employeeUuid, documentUuid });
      invalidateEmployeeDocuments(queryClient, employeeUuid);
    },
    onError: (error, documentUuid) => {
      logEvent('employee_document_delete_error', {
        employeeUuid,
        documentUuid,
        message: (error as Error).message,
      });
    },
  });
};

export const useRequestEmployeeDocumentAccessOtp = (employeeUuid: string) =>
  useMutation<EmployeeDocumentAccessOtpIssue, Error, string>({
    mutationFn: (documentUuid: string) =>
      employeesApi.requestEmployeeDocumentAccessOtp(employeeUuid, documentUuid),
    onSuccess: (_, documentUuid) => {
      logEvent('employee_document_access_otp_request_success', { employeeUuid, documentUuid });
    },
    onError: (error, documentUuid) => {
      logEvent('employee_document_access_otp_request_error', {
        employeeUuid,
        documentUuid,
        message: error.message,
      });
    },
  });

export const useVerifyEmployeeDocumentAccessOtp = (employeeUuid: string) =>
  useMutation<EmployeeDocumentAccessGrant, Error, { documentUuid: string; otp: string }>({
    mutationFn: ({ documentUuid, otp }) =>
      employeesApi.verifyEmployeeDocumentAccessOtp(employeeUuid, documentUuid, otp),
    onSuccess: (_, variables) => {
      logEvent('employee_document_access_otp_verify_success', {
        employeeUuid,
        documentUuid: variables.documentUuid,
      });
    },
    onError: (error, variables) => {
      logEvent('employee_document_access_otp_verify_error', {
        employeeUuid,
        documentUuid: variables.documentUuid,
        message: error.message,
      });
    },
  });

// ---------- C6: Schedule ----------

export const useEmployeeSchedule = (employeeUuid: string | null) =>
  useQuery<EmployeeScheduleDetail>({
    queryKey: employeesKeys.schedule(employeeUuid || ''),
    queryFn: () => employeesApi.getSchedule(employeeUuid as string),
    enabled: Boolean(employeeUuid),
    staleTime: 30_000,
  });

export const useReplaceWeeklySchedule = (employeeUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeScheduleWriteInput) =>
      employeesApi.replaceWeeklySchedule(employeeUuid, input),
    onSuccess: (detail) => {
      logEvent('schedule_replace_success', {
        employeeUuid: detail.employeeUuid,
        scheduleUuid: detail.scheduleUuid,
      });
      queryClient.setQueryData(employeesKeys.schedule(employeeUuid), detail);
    },
    onError: (error) => {
      logEvent('schedule_replace_error', {
        employeeUuid,
        message: (error as Error).message,
      });
    },
  });
};

export const useUpsertScheduleException = (employeeUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeScheduleExceptionWriteInput) =>
      employeesApi.upsertScheduleException(employeeUuid, input),
    onSuccess: (detail, input) => {
      logEvent('schedule_exception_upsert_success', {
        employeeUuid: detail.employeeUuid,
        date: input.date,
        type: input.type,
      });
      queryClient.setQueryData(employeesKeys.schedule(employeeUuid), detail);
    },
    onError: (error, input) => {
      logEvent('schedule_exception_upsert_error', {
        employeeUuid,
        date: input.date,
        message: (error as Error).message,
      });
    },
  });
};

const invalidateAssignments = (
  queryClient: ReturnType<typeof useQueryClient>,
  employeeUuid: string,
  serviceUuid: string,
): void => {
  // Both surfaces share the same join row, so invalidate both axes whenever
  // either side mutates. Cheap because list endpoints return small payloads.
  queryClient.invalidateQueries({ queryKey: employeesKeys.employeeAssignments(employeeUuid) });
  queryClient.invalidateQueries({ queryKey: employeesKeys.serviceAssignments(serviceUuid) });
};

export const useEmployeeAssignments = (employeeUuid: string | null) =>
  useQuery<AssignmentRow[]>({
    queryKey: employeesKeys.employeeAssignments(employeeUuid || ''),
    queryFn: () => employeesApi.listEmployeeAssignments(employeeUuid as string),
    enabled: Boolean(employeeUuid),
    staleTime: 30_000,
  });

export const useServiceAssignments = (serviceUuid: string | null) =>
  useQuery<AssignmentRow[]>({
    queryKey: employeesKeys.serviceAssignments(serviceUuid || ''),
    queryFn: () => employeesApi.listServiceAssignments(serviceUuid as string),
    enabled: Boolean(serviceUuid),
    staleTime: 30_000,
  });

export const useServicePicker = (enabled: boolean) =>
  useQuery<ServicePickerRow[]>({
    queryKey: employeesKeys.servicePicker(),
    queryFn: () => employeesApi.listActiveServices(),
    enabled,
    staleTime: 60_000,
  });

export const useAssignServiceToEmployee = (employeeUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceUuid, input }: { serviceUuid: string; input?: AssignmentWriteInput }) =>
      employeesApi.assignServiceToEmployee(employeeUuid, serviceUuid, input || {}),
    onSuccess: (row) => {
      logEvent('assignment_create_success', {
        employeeUuid: row.employeeUuid,
        serviceUuid: row.serviceUuid,
        assignmentUuid: row.assignmentUuid,
      });
      invalidateAssignments(queryClient, row.employeeUuid, row.serviceUuid);
    },
    onError: (error) => {
      logEvent('assignment_create_error', {
        employeeUuid,
        message: (error as Error).message,
      });
    },
  });
};

export const useAssignEmployeeToService = (serviceUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeUuid, input }: { employeeUuid: string; input?: AssignmentWriteInput }) =>
      employeesApi.assignEmployeeToService(serviceUuid, employeeUuid, input || {}),
    onSuccess: (row) => {
      logEvent('assignment_create_success', {
        employeeUuid: row.employeeUuid,
        serviceUuid: row.serviceUuid,
        assignmentUuid: row.assignmentUuid,
      });
      invalidateAssignments(queryClient, row.employeeUuid, row.serviceUuid);
    },
    onError: (error) => {
      logEvent('assignment_create_error', {
        serviceUuid,
        message: (error as Error).message,
      });
    },
  });
};

export const useRemoveAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeUuid, serviceUuid }: { employeeUuid: string; serviceUuid: string }) =>
      employeesApi.removeEmployeeAssignment(employeeUuid, serviceUuid),
    onSuccess: (_, { employeeUuid, serviceUuid }) => {
      logEvent('assignment_remove_success', { employeeUuid, serviceUuid });
      invalidateAssignments(queryClient, employeeUuid, serviceUuid);
    },
    onError: (error) => {
      logEvent('assignment_remove_error', { message: (error as Error).message });
    },
  });
};

export const useDeleteScheduleException = (employeeUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => employeesApi.deleteScheduleException(employeeUuid, date),
    onSuccess: (detail, date) => {
      logEvent('schedule_exception_delete_success', {
        employeeUuid: detail.employeeUuid,
        date,
      });
      queryClient.setQueryData(employeesKeys.schedule(employeeUuid), detail);
    },
    onError: (error, date) => {
      logEvent('schedule_exception_delete_error', {
        employeeUuid,
        date,
        message: (error as Error).message,
      });
    },
  });
};
